import { useEffect, useRef, useSyncExternalStore } from "react";
import { RiskStage, type StageEntry } from "@helm/ui";

/**
 * The Settings → Risk limits explicit save (one per window). The limits, firm rules and coach forms
 * register here when shown inside the group (`stageKey`); the group's "Save risk limits" /
 * "Discard" bar and the Settings dialog's close guard read it. Mobile and other hosts that render
 * those forms without a `stageKey` keep each form's own buttons.
 */
export const riskStage = new RiskStage();

const subscribe = (l: () => void): (() => void) => riskStage.subscribe(l);
const SEP = "\u0000";

/** Staged parts and saving state; re-renders on those changes only (never on market data). */
export function useRiskStageState(): { dirty: boolean; busy: boolean; labels: string[] } {
  const joined = useSyncExternalStore(subscribe, () => riskStage.dirtyLabels.join(SEP));
  const busy = useSyncExternalStore(subscribe, () => riskStage.busy);
  return { dirty: joined !== "", busy, labels: joined === "" ? [] : joined.split(SEP) };
}

/**
 * Register a form with the group's save while `key` is set. The stage always calls the latest
 * render's save / discard; it is told only when the dirty flag changes.
 */
export function useRiskStageEntry(key: string | undefined, entry: StageEntry): void {
  const latest = useRef(entry);
  latest.current = entry;
  const dirty = entry.dirty;
  const proxy = (d: boolean): StageEntry => ({
    label: latest.current.label,
    dirty: d,
    save: () => latest.current.save(),
    discard: () => {
      latest.current.discard();
    },
  });
  const proxyRef = useRef(proxy);
  proxyRef.current = proxy;
  useEffect(() => {
    if (key === undefined) return;
    return riskStage.register(key, proxyRef.current(false));
  }, [key]);
  useEffect(() => {
    if (key === undefined) return;
    riskStage.update(key, proxyRef.current(dirty));
  }, [key, dirty]);
}
